/* eslint-disable no-underscore-dangle */
import React from 'react';
import { GroupController, TeamController } from 'api';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { GroupInfo, TeamInfo } from '../Hooks';

export type ModalType = 'group' | 'team';

export type ModalMode = 'add' | 'edit';

export type TeamGroupModalContext = {
  working: boolean;
  setWorking: (working: boolean) => void;
  modalVisible: boolean;
  openModal: (type: ModalType, mode: ModalMode, editId?: string) => void;
  closeModal: () => void;
  resetModal: () => void;
  editId: string;
  setEditId: (editId: string) => void;
  type: ModalType;
  mode: ModalMode;
  save: (info: GroupInfo | TeamInfo) => void;
  info: GroupInfo | TeamInfo;
  setInfo: (info: GroupInfo | TeamInfo) => void;
};

export const TeamGroupModalContext = React.createContext<TeamGroupModalContext>(
  {} as any,
);

export const TeamGroupModalContextProvider: React.FC = ({ children }) => {
  const [modalVisible, setModalVisible] = React.useState(false);
  const [editId, setEditId] = React.useState<string | undefined>(undefined);
  const [working, setWorking] = React.useState(false);
  const [type, setType] = React.useState<ModalType>('group');
  const [mode, setMode] = React.useState<ModalMode>('add');
  const [info, setInfo] = React.useState<GroupInfo | TeamInfo | undefined>(
    undefined,
  );

  const resetModal = () => {
    setEditId(undefined);
    setWorking(false);
    setType('group');
    setMode('add');
  };

  const openModal = (
    modalType: ModalType,
    modalMode: ModalMode,
    id: string,
  ) => {
    resetModal();
    setEditId(id);
    setType(modalType);
    setMode(modalMode);
    setModalVisible(true);
  };

  const closeModal = () => {
    resetModal();
    setModalVisible(false);
  };

  const save = async (updates: GroupInfo | TeamInfo) => {
    setWorking(true);

    const _createGroup = async (_updates: GroupInfo) => {
      const userIds = _updates.users.map(({ id }) => id);
      return GroupController.save({ name: _updates.name, users: userIds });
    };

    const _createTeam = async (
      _updates: TeamInfo & { usersToAdd?: string[]; usersToRemove?: string[] },
    ) => TeamController.save({
        name: _updates.name,
        email: _updates.email,
        website: _updates.website,
        phone: _updates.phone,
        teamMlsId: _updates.teamMlsId,
        users: _updates.usersToAdd || [],
      });

    const _updateGroup = async (_updates: GroupInfo) => {
      const userIds = _updates.users.map(({ id }) => id);
      return GroupController.save({ ..._updates, users: userIds });
    };

    const _updateTeam = async (
      _updates: TeamInfo & { usersToAdd?: string[]; usersToRemove?: string[] },
    ) => TeamController.update(_updates.id, _updates);

    try {
      if (mode === 'edit') {
        if (type === 'group') await _updateGroup(updates as GroupInfo);
        if (type === 'team') await _updateTeam(updates as TeamInfo);
      }
      if (mode === 'add') {
        if (type === 'group') await _createGroup(updates as GroupInfo);
        if (type === 'team') await _createTeam(updates as TeamInfo);
      }
      message.success(`Successfully ${mode}ed ${type}`);
    } catch (err) {
      const msg = `Could not ${mode} ${type}`;
      errorReporter.error(msg);
      message.error(msg);
    }
    setWorking(false);
    setModalVisible(false);
  };

  return (
    <TeamGroupModalContext.Provider
      value={{
        working,
        modalVisible,
        editId,
        openModal,
        closeModal,
        resetModal,
        setEditId,
        setWorking,
        type,
        mode,
        save,
        info,
        setInfo,
      }}
    >
      {children}
    </TeamGroupModalContext.Provider>
  );
};
